import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import { useDispatch, useSelector } from 'react-redux';
import { initiateTrim, pollTrimStatus, downloadTrimmedVideo } from '../../store/videoSlice';

const VideoTrimmer = ({ video, onClose }) => {
  const dispatch = useDispatch();
  const videoRef = useRef(null);

  const trimming = useSelector((state) => state.video.trimming);
  const trimmedVideoUrl = useSelector((state) => state.video.trimmedVideos[video.id]);
  const downloadProgress = useSelector((state) => state.video.downloadProgress[video.id] || 0);
  const downloadStatus = useSelector((state) => state.video.downloadStatus[video.id] || 'idle');
  const error = useSelector((state) => state.video.error);

  const [startMinutes, setStartMinutes] = useState(0);
  const [startSeconds, setStartSeconds] = useState(0);
  const [endMinutes, setEndMinutes] = useState(0);
  const [endSeconds, setEndSeconds] = useState(0);

  const handleTrim = () => {
    const startTotalSeconds = startMinutes * 60 + startSeconds;
    const endTotalSeconds = endMinutes * 60 + endSeconds;

    if (startTotalSeconds >= endTotalSeconds) {
      toast.error('Start time must be less than end time.');
      return;
    }
    if (videoRef.current && endTotalSeconds > videoRef.current.duration) {
      toast.error('End time exceeds video duration.');
      return;
    }
    dispatch(initiateTrim({ videoId: video.id, endSeconds: endTotalSeconds }));
  };

  useEffect(() => {
    let intervalId;
    if (trimming.isTrimming && trimming.trackingId) {
      intervalId = setInterval(() => {
        dispatch(pollTrimStatus({ trackingId: trimming.trackingId, videoId: video.id }));
      }, 5000);
    }
    return () => { if (intervalId) clearInterval(intervalId); };
  }, [trimming.isTrimming, trimming.trackingId, dispatch, video.id]);

  const handleDownload = () => {
    if (trimmedVideoUrl) {
      dispatch(downloadTrimmedVideo({ videoId: video.id, trimmedVideoUrl }));
    } else {
      toast.error('Trimmed video URL is not available.');
    }
  };

  return (
    <TrimmerContainer>
      {error && <ErrorMessage role="alert">{error}</ErrorMessage>}

      {!trimmedVideoUrl && (
        <>
          <VideoPlayer
            ref={videoRef}
            src={video.preSignedS3Url}
            controls
            onLoadedMetadata={() => {
              const totalDuration = videoRef.current.duration;
              setEndMinutes(Math.floor(totalDuration / 60));
              setEndSeconds(Math.floor(totalDuration % 60));
            }}
          />

          <div className="flex flex-col gap-3">
            {/* Start Time */}
            <div className="flex flex-col gap-2">
              <label className="text-sm font-medium text-text-primary">Start Time:</label>
              <div className="flex items-center gap-2">
                <TimeInput
                  type="number"
                  value={startMinutes}
                  min="0"
                  onChange={(e) => setStartMinutes(Number(e.target.value))}
                  placeholder="Min"
                />
                <span className="text-md mx-1">:</span>
                <TimeInput
                  type="number"
                  value={startSeconds}
                  min="0"
                  max="59"
                  onChange={(e) => setStartSeconds(Number(e.target.value))}
                  placeholder="Sec"
                />
              </div>
            </div>

            {/* End Time */}
            <div className="flex flex-col gap-2">
              <label className="text-sm font-medium text-text-primary">End Time:</label>
              <div className="flex items-center gap-2">
                <TimeInput
                  type="number"
                  value={endMinutes}
                  min="0"
                  onChange={(e) => setEndMinutes(Number(e.target.value))}
                  placeholder="Min"
                />
                <span className="text-md mx-1">:</span>
                <TimeInput
                  type="number"
                  value={endSeconds}
                  min="0"
                  max="59"
                  onChange={(e) => setEndSeconds(Number(e.target.value))}
                  placeholder="Sec"
                />
              </div>
            </div>

            <TrimButton onClick={handleTrim} disabled={trimming.isTrimming}>
              {trimming.isTrimming ? 'Trimming...' : 'Trim Video'}
            </TrimButton>
          </div>

          {trimming.isTrimming && (
            <div className="mt-4">
              <div className="flex items-center gap-2">
                <div className="flex-1 h-3 bg-gray-200 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-secondary rounded-full transition-all duration-300"
                    style={{ width: `${Math.max(0, Math.min(100, trimming.progress))}%` }}
                  />
                </div>
                <span className="text-xs text-text-secondary min-w-[40px] text-right">
                  {trimming.progress}%
                </span>
              </div>
            </div>
          )}
        </>
      )}

      {trimmedVideoUrl && (
        <div>
          <p className="text-sm text-text-primary mb-3">Video trimming is complete.</p>
          <VideoPlayer src={trimmedVideoUrl} controls />

          <div className="flex gap-3 mt-4 flex-wrap sm:flex-col">
            {downloadStatus === 'loading' && (
              <div className="w-full">
                <div className="flex items-center gap-2">
                  <div className="flex-1 h-3 bg-gray-200 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-secondary rounded-full transition-all duration-300"
                      style={{ width: `${Math.max(0, Math.min(100, downloadProgress))}%` }}
                    />
                  </div>
                  <span className="text-xs text-text-secondary min-w-[40px] text-right">
                    {downloadProgress}%
                  </span>
                </div>
              </div>
            )}

            <StyledButton onClick={handleDownload} disabled={downloadStatus === 'loading'}>
              {downloadStatus === 'loading' ? 'Downloading...' : 'Download'}
            </StyledButton>
            <StyledCloseButton onClick={onClose}>Close</StyledCloseButton>
          </div>
        </div>
      )}
    </TrimmerContainer>
  );
};

VideoTrimmer.propTypes = {
  video: PropTypes.shape({
    id: PropTypes.number.isRequired,
    preSignedS3Url: PropTypes.string.isRequired,
    duration: PropTypes.number.isRequired,
    title: PropTypes.string.isRequired,
  }).isRequired,
  onClose: PropTypes.func.isRequired,
};

export default VideoTrimmer;

// ─── Styled Components (no Bootstrap dependencies) ───────────────────────────

const TrimmerContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.md};
  padding: ${({ theme }) => theme.spacing.lg};
  background-color: ${({ theme }) => theme.colors.secondaryBackground};
  border-radius: ${({ theme }) => theme.spacing.sm};
  box-shadow: ${({ theme }) => theme.shadows.medium};

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    padding: ${({ theme }) => theme.spacing.md};
  }
`;

const VideoPlayer = styled.video`
  width: 100%;
  border-radius: ${({ theme }) => theme.spacing.xs};
  background-color: ${({ theme }) => theme.colors.primaryBackground};
`;

const TimeInput = styled.input`
  padding: ${({ theme }) => theme.spacing.sm};
  border-radius: ${({ theme }) => theme.spacing.xs};
  border: 1px solid ${({ theme }) => theme.colors.border};
  width: 80px;
  font-size: ${({ theme }) => theme.fontSizes.md};

  &:focus {
    border-color: ${({ theme }) => theme.colors.secondary};
    outline: none;
    box-shadow: 0 0 0 2px ${({ theme }) => theme.colors.secondary}30;
  }
`;

const TrimButton = styled.button`
  margin-top: ${({ theme }) => theme.spacing.md};
  padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.md};
  background-color: ${({ theme }) => theme.colors.primary};
  border: none;
  border-radius: ${({ theme }) => theme.spacing.xs};
  font-size: ${({ theme }) => theme.fontSizes.md};
  color: #fff;
  cursor: pointer;
  transition: background-color 0.3s ease;

  &:hover {
    background-color: ${({ theme }) => theme.colors.secondary};
  }

  &:disabled {
    background-color: ${({ theme }) => theme.colors.disabledBackground};
    cursor: not-allowed;
  }
`;

const StyledButton = styled.button`
  padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.md};
  background-color: ${({ theme }) => theme.colors.secondary};
  border: none;
  border-radius: ${({ theme }) => theme.spacing.xs};
  font-size: ${({ theme }) => theme.fontSizes.md};
  color: #fff;
  cursor: pointer;
  transition: background-color 0.3s ease;

  &:hover {
    background-color: ${({ theme }) => theme.colors.accents[0]};
  }

  &:disabled {
    background-color: ${({ theme }) => theme.colors.disabledBackground};
    cursor: not-allowed;
  }
`;

const StyledCloseButton = styled(StyledButton)`
  background-color: ${({ theme }) => theme.colors.accents[2]};

  &:hover {
    background-color: ${({ theme }) => theme.colors.darkBackground};
  }
`;

const ErrorMessage = styled.p`
  color: ${({ theme }) => theme.colors.error};
  font-size: ${({ theme }) => theme.fontSizes.sm};
  text-align: center;
`;
